import React, { useContext } from 'react'
import { useRouter } from 'next/router'
import { Link } from 'pcln-design-system'
import {
  ContentfulRichText,
  ICallToActionType
} from '@pcln/contentful-components'
import { MerchModule } from '@pcln/marketing'
import analytics from '@/shared-utils/analytics'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import { CustomToutType } from '@/types'
import {
  ANALYTICS_CATEGORY_FLIGHTS,
  ANALYTICS_CATEGORY_HOTELS,
  ANALYTICS_CATEGORY_PACKAGES,
  ANALYTICS_CATEGORY_HOME
} from '@/constants/analytics'
import AppleStore from './graphics/AppleStoreSvg'
import PlayStore from './graphics/PlayStoreSvg'
import useGoogleAnalyticsTracking from './useGoogleAnalyticsTracking'
import LayoutComponentWrapper from '../LayoutComponentWrapper'

const Graphics = {
  AppleStore,
  PlayStore
}

function renderGraphic(svgName: 'AppleStore' | 'PlayStore') {
  const SVG = Graphics[svgName]
  return <SVG />
}

export const ANALYTICS_CATEGORIES = {
  '/flights': ANALYTICS_CATEGORY_FLIGHTS,
  '/hotels': ANALYTICS_CATEGORY_HOTELS,
  '/vacationpackages': ANALYTICS_CATEGORY_PACKAGES,
  '/': ANALYTICS_CATEGORY_HOME
} as const

export type ANALYTICS_CATEGORY_TYPE =
  | typeof ANALYTICS_CATEGORY_FLIGHTS
  | typeof ANALYTICS_CATEGORY_HOTELS
  | typeof ANALYTICS_CATEGORY_PACKAGES
  | typeof ANALYTICS_CATEGORY_HOME

function ToutWrapper({
  content,
  credCardModWrapDefaultDisplay = false
}: {
  content: CustomToutType
  credCardModWrapDefaultDisplay?: boolean
}) {
  const router = useRouter()
  const pathname = router?.pathname
  const googleAnalyticsCategory: ANALYTICS_CATEGORY_TYPE =
    ANALYTICS_CATEGORIES[pathname as keyof typeof ANALYTICS_CATEGORIES]
  const { googleAnalyticsTracking, callsToAction } = content
  useGoogleAnalyticsTracking(googleAnalyticsTracking)
  const moduleButtons = callsToAction && (
    <>
      {callsToAction.map((cta: ICallToActionType) => {
        const linkVariation = cta.svgName ? 'link' : 'fill'
        return (
          <Link
            mr={2}
            key={cta.ariaLabel}
            variation={linkVariation}
            href={cta.url}
            target={cta.openInNewTab ? '_blank' : undefined}
            rel={cta.openInNewTab ? 'noopener' : undefined}
            aria-label={cta.ariaLabel}
            onClick={() => {
              if (cta.googleAnalyticsTracking?.action) {
                analytics.fireEvent({
                  action: cta.googleAnalyticsTracking.action,
                  category:
                    cta.googleAnalyticsTracking.category ||
                    googleAnalyticsCategory,
                  label: cta.googleAnalyticsTracking.label
                })
              }
            }}
          >
            {cta.svgName
              ? renderGraphic(cta.svgName as 'AppleStore' | 'PlayStore')
              : cta.label}
          </Link>
        )
      })}
    </>
  )
  const { isMobile } = useContext(BootstrapDataContext)
  const { displayOption, image, body, ...props } = content

  const customCreditCardDisplayProps = credCardModWrapDefaultDisplay
    ? {
        boxProps: { p: 4 },
        textAlign: 'left'
      }
    : {
        boxProps: {
          p: 4
        }
      }
  return (
    <LayoutComponentWrapper>
      <MerchModule
        {...props}
        {...customCreditCardDisplayProps}
        isBox
        minImageHeight="400px"
        mediaDisplay={displayOption}
        imageUrl={
          image?.asset?.file?.url
            ? `${image.asset.file.url}?h=600&q=70&fm=webp`
            : undefined
        }
        imageAlt={image?.altText}
        callToAction={image?.callToAction}
        headingTextStyle="heading2"
        taglineTextStyle="overline"
        bodyLineHeight="25px"
        isMobile={isMobile}
        headingMB="0px"
        body={
          <ContentfulRichText
            color="text.dark"
            textStyle="article"
            fontSize={3}
            body={body as any}
          />
        }
        actions={moduleButtons}
      />
    </LayoutComponentWrapper>
  )
}

export default ToutWrapper
